import { useState, MouseEvent } from 'react';

import { Business, Logout } from '@mui/icons-material';
import { Avatar, Box, IconButton, ListItemIcon, Menu, Tooltip, MenuItem, ListItemText } from '@mui/material';
import { User as UserIcon } from 'react-feather';

import { googleLogout } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';

import { getRoutePath } from '~/router/routes';
import { getConfig } from '~/services/config';
import { useManageChat } from '~/modules/@shared/hooks/useManageChat';
import { getQueryClient } from '~/services/query-client';
import { GASendEvent, GAEventPath } from '~/analytics';
import { useGetUserProfileQuery } from '~/services/staff.ts';
import { useGetLocationId } from '~/modules/auth';
import { User } from '~/services/types';

type Props = {
  user: User;
};

export default function UserDropdown({ user }: Props) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const openUserProfileSetting = Boolean(anchorEl);
  const { clearToken } = getConfig();
  const { locationId } = useGetLocationId();

  const { tryChat: tryCloseChat } = useManageChat({ close: true, disabled: true });

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    googleLogout();
    tryCloseChat();
    const queryClient = getQueryClient();
    queryClient.resetQueries();
    queryClient.invalidateQueries({ queryKey: ['user'] });

    GASendEvent(GAEventPath.INTERACTION_LOGOUT, 'UserLogout');

    clearToken();
    navigate(getRoutePath('auth.login'));
  };

  const { data: userProfile } = useGetUserProfileQuery({ locationId, userId: user.id });

  return (
    <Box>
      <Tooltip title="Profile">
        <IconButton
          sx={{ p: 0 }}
          onClick={handleClick}
          aria-controls={openUserProfileSetting ? 'account-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={openUserProfileSetting ? 'true' : undefined}>
          <Avatar sx={{ backgroundColor: 'warning.main' }} src={userProfile?.photo_url} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={openUserProfileSetting}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            sx: {
              overflow: 'visible',
              mt: 1.5,
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                backgroundColor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
        <MenuItem sx={{ padding: '1em' }} onClick={() => navigate(getRoutePath('myprofile'))}>
          <ListItemIcon>
            <UserIcon style={{ width: '1.5em' }} />
          </ListItemIcon>
          <ListItemText primary="My Profile"></ListItemText>
        </MenuItem>
        <MenuItem sx={{ padding: '1em' }} onClick={() => navigate(getRoutePath('settings.business'))}>
          <ListItemIcon>
            <Business fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Business settings"></ListItemText>
        </MenuItem>
        <MenuItem
          sx={{ padding: '1em' }}
          onClick={() => {
            void handleLogout();
          }}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Logout"></ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}
